import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { EncryptionProvider } from "./contexts/EncryptionContext";
import { CookieConsent } from "./components/CookieConsent";
import { OfflineBanner } from "./components/OfflineBanner";
import { InstallBanner } from "./components/InstallBanner";

// Eager load landing page for best FCP/LCP
import MarketingHome from "./pages/marketing/Home";

// Lazy load all other routes for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const WeeklyPlanner = lazy(() => import("./pages/WeeklyPlanner"));
const WeeklyAdminSession = lazy(() => import("./pages/WeeklyAdminSession"));
const CalendarView = lazy(() => import("./pages/CalendarView"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Settings = lazy(() => import("./pages/Settings"));
const FamilySharing = lazy(() => import("./pages/FamilySharing"));
const FamilyCalendarView = lazy(() => import("./pages/FamilyCalendarView"));
const MagicInbox = lazy(() => import("./pages/MagicInbox"));
const RoutineBuilder = lazy(() => import("./pages/RoutineBuilder"));
const InsightsDashboard = lazy(() => import("./pages/InsightsDashboard"));
const DocumentVault = lazy(() => import("./pages/DocumentVault"));
const FutureTasks = lazy(() => import("./pages/FutureTasks"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const MyTasks = lazy(() => import("./pages/MyTasks"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Install = lazy(() => import("./pages/Install"));
const RedeemCode = lazy(() => import("./pages/RedeemCode"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MarketingPricing = lazy(() => import("./pages/marketing/Pricing"));
const MarketingFeatures = lazy(() => import("./pages/marketing/Features"));
const MarketingAbout = lazy(() => import("./pages/marketing/About"));
const Privacy = lazy(() => import("./pages/marketing/Privacy"));
const Terms = lazy(() => import("./pages/marketing/Terms"));
const Contact = lazy(() => import("./pages/marketing/Contact"));
const Partners = lazy(() => import("./pages/marketing/Partners"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <EncryptionProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<MarketingHome />} />
              <Route path="/app" element={<Suspense fallback={null}><Index /></Suspense>} />
              <Route path="/auth" element={<Suspense fallback={null}><Auth /></Suspense>} />
              <Route path="/onboarding" element={<Suspense fallback={null}><Onboarding /></Suspense>} />
              <Route path="/weekly-planner" element={<Suspense fallback={null}><WeeklyPlanner /></Suspense>} />
              <Route path="/weekly-session" element={<Suspense fallback={null}><WeeklyAdminSession /></Suspense>} />
              <Route path="/calendar" element={<Suspense fallback={null}><CalendarView /></Suspense>} />
              <Route path="/analytics" element={<Suspense fallback={null}><Analytics /></Suspense>} />
              <Route path="/settings" element={<Suspense fallback={null}><Settings /></Suspense>} />
              <Route path="/family" element={<Suspense fallback={null}><FamilySharing /></Suspense>} />
              <Route path="/family-calendar" element={<Suspense fallback={null}><FamilyCalendarView /></Suspense>} />
              <Route path="/magic-inbox" element={<Suspense fallback={null}><MagicInbox /></Suspense>} />
              <Route path="/routines" element={<Suspense fallback={null}><RoutineBuilder /></Suspense>} />
              <Route path="/insights" element={<Suspense fallback={null}><InsightsDashboard /></Suspense>} />
              <Route path="/documents" element={<Suspense fallback={null}><DocumentVault /></Suspense>} />
              <Route path="/future-tasks" element={<Suspense fallback={null}><FutureTasks /></Suspense>} />
              <Route path="/accept-invite" element={<Suspense fallback={null}><AcceptInvite /></Suspense>} />
              <Route path="/my-tasks" element={<Suspense fallback={null}><MyTasks /></Suspense>} />
              <Route path="/subscription" element={<Suspense fallback={null}><Subscription /></Suspense>} />
              <Route path="/pricing" element={<Suspense fallback={null}><MarketingPricing /></Suspense>} />
              <Route path="/features" element={<Suspense fallback={null}><MarketingFeatures /></Suspense>} />
              <Route path="/about" element={<Suspense fallback={null}><MarketingAbout /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={null}><Privacy /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={null}><Terms /></Suspense>} />
              <Route path="/contact" element={<Suspense fallback={null}><Contact /></Suspense>} />
              <Route path="/partners" element={<Suspense fallback={null}><Partners /></Suspense>} />
              <Route path="/install" element={<Suspense fallback={null}><Install /></Suspense>} />
              <Route path="/redeem" element={<Suspense fallback={null}><RedeemCode /></Suspense>} />
              <Route path="/welcome" element={<Navigate to="/" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
            </Routes>
            <CookieConsent />
            <OfflineBanner />
            <InstallBanner />
          </BrowserRouter>
        </TooltipProvider>
      </EncryptionProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
